/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { WbAutocomplete } from '@agilelab/plugin-wb-platform';
import { GetEntityFacetsRequest } from '@mainstage/catalog-client';
import { makeValidator } from '@mainstage/catalog-model';
import { useApi } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { extractCustomProperties, isHidden } from '../../utils';
import { EntityTagsPickerProps } from './schema';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

/**
 * The underlying component that is rendered in the form for the `EntityTagsPicker`
 * field extension.
 *
 * @public
 */
export const EntityTagsPicker = (props: EntityTagsPickerProps) => {
  const {
    schema: { title = 'Tags', description = undefined },
    formData,
    onChange,
    uiSchema,
    idSchema,
    formContext,
  } = props;
  const catalogApi = useApi(catalogApiRef);

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [idSchema, rootSchema]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState(false);
  const tagValidator = makeValidator().isValidTag;
  const kinds = uiSchema['ui:options']?.kinds;
  const showCounts = uiSchema['ui:options']?.showCounts;
  const customProps = extractCustomProperties(uiSchema);
  const helperText =
    uiSchema['ui:options']?.helperText ??
    "Add any relevant tags, hit 'Enter' to add new tags. Valid format: [a-z0-9+#] separated by [-], at most 63 characters";
  const descriptionLabel = description
    ? `${description}. ${helperText}`
    : helperText;

  const { loading, value: existingTags } = useAsync(async () => {
    const facet = 'metadata.tags';
    const tagsRequest: GetEntityFacetsRequest = { facets: [facet] };
    if (kinds) {
      tagsRequest.filter = { kind: kinds };
    }

    const { facets } = await catalogApi.getEntityFacets(tagsRequest);

    const tagFacets = Object.fromEntries(
      facets[facet].map(({ value, count }) => [value, count]),
    );

    setTagOptions(
      Object.keys(tagFacets).sort((a, b) =>
        showCounts ? tagFacets[b] - tagFacets[a] : a.localeCompare(b),
      ),
    );

    return tagFacets;
  });

  const setTags = useCallback(
    (_: ChangeEvent<{}>, values: string[] | null) => {
      // Reset error state in case all tags were removed
      let hasError = false;
      let addDuplicate = false;
      const currentTags = formData || [];

      // If adding a new tag
      if (values?.length && currentTags.length < values.length) {
        const newTag = (values[values.length - 1] = values[values.length - 1]
          .toLocaleLowerCase('en-US')
          .trim());
        hasError = !tagValidator(newTag);
        addDuplicate = currentTags.indexOf(newTag) !== -1;
      }

      setInputError(hasError);
      setInputValue(!hasError ? '' : inputValue);
      if (!hasError && !addDuplicate) {
        const newValues = values || [];

        // Notify dependency manager to propagate changes to dependents, or fallback to onChange
        if (dependencyManager?.notifyUpdate && fieldPath) {
          dependencyManager.notifyUpdate({
            fieldPath,
            value: newValues,
            source: 'user',
            intent: 'manual',
          });
        } else {
          onChange(newValues);
        }
      }
    },
    [
      formData,
      tagValidator,
      inputValue,
      onChange,
      dependencyManager,
      fieldPath,
    ],
  );

  // Initialize field to always return an array
  useEffectOnce(() => {
    if (!formData) onChange([]);
  });

  return (
    <WbAutocomplete<string, true, false, true>
      style={{
        display: isHidden(uiSchema) ? 'none' : undefined,
        width: '100%',
        height: '100%',
      }}
      multiple
      freeSolo
      onChange={setTags}
      value={formData || []}
      inputValue={inputValue}
      loading={loading}
      options={tagOptions}
      helperText={descriptionLabel}
      label={title}
      error={inputError}
      onTextFieldChange={e => setInputValue(e.target.value)}
      getOptionLabel={option =>
        showCounts ? `${option} (${existingTags?.[option]})` : option
      }
      {...customProps}
    />
  );
};
